export default function MetodologiaSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            La Necessità di un Nuovo Standard
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            I metodi tradizionali di valutazione del brand presentano lacune critiche che compromettono 
            l'accuratezza e la difendibilità delle perizie aziendali e sopratutto non integrano tutti i kpi reputazionali emersi con l'evoluzione del marketing.
          </p>
          <div className="mt-8 text-left max-w-4xl mx-auto space-y-6">
            <p className="text-base text-gray-700 leading-relaxed">
              Brand Capital si posiziona come una piattaforma di governance del brand dedicata alle piccole e medie imprese, con l'obiettivo di trasformare il brand da elemento prevalentemente narrativo e comunicativo a vero e proprio asset economico, misurabile e integrato nei processi decisionali aziendali. Questo posizionamento nasce direttamente dall'analisi di un divario strutturale sempre più evidente nel contesto delle PMI: da un lato la crescente rilevanza economica del brand, dall'altro la mancanza di strumenti strutturati in grado di gestirlo, misurarlo e renderlo leggibile all'interno delle decisioni strategiche e finanziarie dell'impresa.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              <strong>Brand Capital</strong> si colloca in uno spazio di mercato pensato specificamente per il contesto delle Micro e PMI italiane e soprattutto con una mission differente da tutti. difatti Brand Capital si focalizza sulla governance del brand come strumento per la costruzione del patrimonio aziendale.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              L'elemento distintivo non è solo la valutazione del brand, ma la sua trasformazione in asset governabile nel tempo, attraverso strumenti di monitoraggio, dashboard e modelli di governance accessibili.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              Quindi mentre nel mercato il Brand viene trattato come uno strumento per attirare investitori e capitali, Brand Capital tratta il Brand come un assett intangibile in grado di aumentare il patrimonio aziendale.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-slate-900 mb-8">Problematiche Attuali</h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-close-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Indicatori Incompleti</h4>
                  <p className="text-gray-600">I metodi esistenti non considerano tutti i fattori che contribuiscono al valore del brand, limitandosi a parametri parziali e obsoleti.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-close-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Sovravalutazione Sistematica</h4>
                  <p className="text-gray-600">Metodologie che gonfiano artificialmente i valori, creando aspettative irrealistiche e vulnerabilità durante i controlli.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <i className="ri-close-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Indifendibilità Legale</h4>
                  <p className="text-gray-600">Approcci che non resistono al vaglio di revisori qualificati, banche e organi di controllo, mettendo a rischio l'azienda.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/fcdac6f4aebafb5b1c7f46d68fbc801d.jpg"
              alt="Problematiche dei metodi tradizionali"
              className="w-full h-96 object-cover rounded-2xl shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Advanced%20mathematical%20algorithm%20visualization%20with%20complex%20formulas%2C%20data%20flow%20diagrams%2C%20and%20brand%20valuation%20calculations%20on%20a%20digital%20interface%2C%20sophisticated%20financial%20modeling%20software%20displaying%20interconnected%20metrics%2C%20modern%20analytical%20dashboard%20with%20precision%20indicators%2C%20scientific%20approach%20to%20brand%20assessment&width=800&height=600&seq=soluzione-bci&orientation=landscape"
                alt="Brand Capital Index - La soluzione"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#E879F9] to-[#22C55E] text-white rounded-full text-sm font-medium mb-6">
                <i className="ri-lightbulb-line mr-2"></i>
                La Soluzione BCI
              </div>
              
              <h3 className="text-3xl font-bold text-slate-900 mb-6">
                Brand Capital Index: Precisione e Affidabilità
              </h3>
              
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Il BCI integra <strong>tutti gli indicatori rilevanti</strong> in un algoritmo proprietario che garantisce 
                valutazioni accurate, conservative e scientificamente fondate. Una metodologia che supera 
                i limiti dei sistemi tradizionali offrendo la massima affidabilità professionale.
              </p>

              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-[#22C55E] rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700 font-medium">Algoritmo multi-dimensionale brevettato</span>
                </div>
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-[#22C55E] rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700 font-medium">Valutazioni conservative e difendibili</span>
                </div>
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-[#22C55E] rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700 font-medium">Certificazione e protezione legale</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}